/**
 * Where this screen sits, and one click back to everything above it.
 *
 * Every trail starts at the dashboard — the component prepends it, so no screen
 * has to remember to, and no screen can leave staff without a route to the root.
 * Pass only the crumbs *below* it.
 *
 *   <PageHeader breadcrumbs={[CRUMB.volunteers, { label: 'Nisha Verma' }]} … />
 *   → Dashboard / Volunteers / Nisha Verma
 *
 * **The last crumb is the current page**, so it is rendered as text rather than a
 * link even when it carries a `to` — a link to the page you are already on is a
 * small lie that staff learn to stop trusting. That means a section screen can
 * pass its own `CRUMB` constant unchanged (`[CRUMB.volunteers]`) and get the right
 * thing, which is what keeps the labels in `constants/breadcrumbs.ts` the single
 * source for both the crumb and the destination it points at.
 *
 * Detail screens should render this in their error branch too, with a generic
 * label for the record that would not load. A record that 404s is exactly when
 * staff most need the way back, and a screen that only draws its breadcrumbs on
 * the happy path strands them.
 */

import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import styles from './Breadcrumbs.module.css';

export interface Crumb {
  label: string;
  /** Omit on the current page. Ignored on the last crumb regardless. */
  to?: string;
}

const DASHBOARD: Crumb = { label: 'Dashboard', to: '/' };

export interface BreadcrumbsProps {
  /** The trail below the dashboard. The last entry is the current page. */
  items: Crumb[];
}

export function Breadcrumbs({ items }: BreadcrumbsProps) {
  if (items.length === 0) return null;

  const trail = [DASHBOARD, ...items];

  return (
    <nav className={styles.breadcrumbs} aria-label="Breadcrumb">
      <ol className={styles.list}>
        {trail.map((crumb, index) => {
          const isCurrent = index === trail.length - 1;

          return (
            <Fragment key={`${crumb.label}-${index}`}>
              <li className={styles.item}>
                {isCurrent || !crumb.to ? (
                  // aria-current tells a screen reader which of these is "here",
                  // which is the whole point of the trail for anyone not seeing it.
                  <span className={styles.current} aria-current={isCurrent ? 'page' : undefined}>
                    {crumb.label}
                  </span>
                ) : (
                  <Link className={styles.link} to={crumb.to}>
                    {crumb.label}
                  </Link>
                )}
              </li>
              {!isCurrent && (
                // Decorative: the list structure already conveys the nesting, so a
                // screen reader announcing "slash" between every crumb is noise.
                <li className={styles.separator} aria-hidden="true">
                  /
                </li>
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
